import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { calendarAPI, agentAPI } from '../../services/api';
import { format, startOfWeek, addDays, startOfMonth, endOfMonth, endOfWeek, isSameMonth, isSameDay, addMonths, subMonths, setHours, setMinutes } from 'date-fns';
import { th } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const { FiChevronLeft, FiChevronRight, FiPlus, FiX, FiCalendar, FiTag, FiAlignLeft, FiClock, FiUser, FiSave, FiEdit2, FiTrash2, FiFilter, FiUsers } = FiIcons;

const EventType = {
  MEETING: 'Meeting',
  TRAINING: 'Training',
  CUSTOMER_VISIT: 'CustomerVisit',
  PRESENTATION: 'Presentation',
  FOLLOW_UP: 'FollowUp',
  APPOINTMENT: 'Appointment',
  CONFERENCE: 'Conference',
  PERSONAL: 'Personal',
  OTHER: 'Other'
};

const EventTypeOptions = Object.values(EventType);

// Shared color functions
const getEventTypeColor = (eventType) => {
  const colors = {
    Meeting: 'bg-blue-500',
    Training: 'bg-orange-500',
    CustomerVisit: 'bg-green-500',
    Presentation: 'bg-purple-500',
    FollowUp: 'bg-yellow-500',
    Appointment: 'bg-indigo-500',
    Conference: 'bg-red-500',
    Personal: 'bg-pink-500',
    Other: 'bg-gray-500',
    Birthday: 'bg-pink-500',
    LicenseExpiry: 'bg-amber-500',
    PolicyExpiry: 'bg-emerald-500'
  };
  return colors[eventType] || colors.Other;
};

const getEventTypeDisplayColor = (eventType) => {
  const colors = {
    Meeting: 'bg-blue-100 text-blue-700',
    Training: 'bg-orange-100 text-orange-700',
    CustomerVisit: 'bg-green-100 text-green-700',
    Presentation: 'bg-purple-100 text-purple-700',
    FollowUp: 'bg-yellow-100 text-yellow-700',
    Appointment: 'bg-indigo-100 text-indigo-700',
    Conference: 'bg-red-100 text-red-700',
    Personal: 'bg-pink-100 text-pink-700',
    Other: 'bg-gray-100 text-gray-700',
    Birthday: 'bg-pink-100 text-pink-700',
    LicenseExpiry: 'bg-amber-100 text-amber-700',
    PolicyExpiry: 'bg-emerald-100 text-emerald-700'
  };
  return colors[eventType] || colors.Other;
};

const getEventTypeLabel = (eventType) => {
  const labels = {
    Meeting: 'ประชุม',
    Training: 'อบรม',
    CustomerVisit: 'พบลูกค้า',
    Presentation: 'นำเสนอ',
    FollowUp: 'ติดตาม',
    Appointment: 'นัดหมาย',
    Conference: 'ประชุมใหญ่',
    Personal: 'ส่วนตัว',
    Other: 'อื่นๆ',
    Birthday: 'วันเกิด',
    LicenseExpiry: 'ใบอนุญาตหมดอายุ',
    PolicyExpiry: 'ครบกำหนดชำระเบี้ย'
  };
  return labels[eventType] || labels.Other;
};

const EventModal = ({ isOpen, onClose, selectedDate, agentId, onEventCreated, eventToEdit = null }) => {
  const [title, setTitle] = useState('');
  const [type, setType] = useState(EventType.MEETING);
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!eventToEdit;

  // Load event data when editing
  useEffect(() => {
    if (eventToEdit) {
      setTitle(eventToEdit.title);
      setType(eventToEdit.type);
      setDescription(eventToEdit.description || '');
      setStartTime(format(new Date(eventToEdit.startTime), 'HH:mm'));
      setEndTime(format(new Date(eventToEdit.endTime), 'HH:mm'));
    } else {
      resetForm();
    }
  }, [eventToEdit]);

  const resetForm = () => {
    setTitle('');
    setType(EventType.MEETING);
    setDescription('');
    setStartTime('09:00');
    setEndTime('10:00');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !startTime || !endTime || !agentId) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }

    setIsSubmitting(true);

    try {
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);

      const startDateTime = setMinutes(setHours(selectedDate, startHour), startMinute);
      const endDateTime = setMinutes(setHours(selectedDate, endHour), endMinute);

      const eventData = {
        title: title.trim(),
        type,
        description: description.trim(),
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        agentId: Number(agentId), // Ensure agentId is a number
        isAllDay: false
      };

      // Debug log to verify data structure
      console.log('Sending event data:', eventData);

      if (isEditMode) {
        await calendarAPI.updateEvent(eventToEdit.id, eventData);
        toast.success('อัพเดตกิจกรรมสำเร็จ');
      } else {
        await calendarAPI.createEvent(eventData);
        toast.success('สร้างกิจกรรมสำเร็จ');
      }
      
      onClose();
      resetForm();
      // Call callback to refresh events
      if (onEventCreated) {
        onEventCreated();
      }
    } catch (error) {
      console.error('Error saving event:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.message || `ไม่สามารถ${isEditMode ? 'อัพเดต' : 'สร้าง'}กิจกรรมได้`);
    } finally {
      setIsSubmitting(false);
    }
  };

  
  if (!isOpen) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        // Close modal when clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => {
          // Prevent modal from closing when clicking inside the modal content
          e.stopPropagation();
        }}
      >
        {/* Header with selected date display */}
        <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <div className="absolute top-4 right-4 z-10">
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-white/20 rounded-full transition-colors duration-200 flex items-center justify-center"
              title="ปิด"
            >
              <SafeIcon icon={FiX} className="text-white" size={22} />
            </button>
          </div>
          
          <div className="pr-12">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={isEditMode ? FiEdit2 : FiCalendar} size={20} />
              <span className="text-sm font-medium opacity-90">
                {isEditMode ? 'แก้ไขกิจกรรม' : 'เพิ่มกิจกรรมใหม่'}
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-1">
              {isEditMode ? 'แก้ไขกิจกรรม' : 'สร้างกิจกรรม'}
            </h2>
            <div className="flex items-center space-x-2 text-blue-100">
              <SafeIcon icon={FiClock} size={16} />
              <span className="text-sm">
                {format(selectedDate, "eeee, d MMMM yyyy", { locale: th })}
              </span>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-white/5 rounded-full"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title Input */}
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
              <SafeIcon icon={FiTag} size={16} />
              <span>หัวข้อกิจกรรม</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors duration-200 placeholder-gray-400"
              placeholder="ระบุชื่อกิจกรรม เช่น ประชุมทีม"
            />
          </div>

          {/* Event Type */}
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
              <div className={`w-4 h-4 rounded-full ${getEventTypeColor(type)}`}></div>
              <span>ประเภทกิจกรรม</span>
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors duration-200 bg-white"
            >
              {EventTypeOptions.map(opt => (
                <option key={opt} value={opt}>{getEventTypeLabel(opt)}</option>
              ))}
            </select>
          </div>

          {/* Time Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                <SafeIcon icon={FiClock} size={16} />
                <span>เริ่มต้น</span>
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors duration-200"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
                <SafeIcon icon={FiClock} size={16} />
                <span>สิ้นสุด</span>
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors duration-200"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700">
              <SafeIcon icon={FiAlignLeft} size={16} />
              <span>รายละเอียด</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="3"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors duration-200 placeholder-gray-400 resize-none"
              placeholder="เพิ่มรายละเอียดเกี่ยวกับกิจกรรม..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors duration-200 font-semibold"
            >
              ยกเลิก
            </button>
            <button 
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 transition-all duration-200 font-semibold flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>กำลังบันทึก...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} size={18} />
                  <span>บันทึกกิจกรรม</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// View Event Modal Component (Read-only)
const ViewEventModal = ({ isOpen, onClose, event, getAgentName }) => {
  if (!isOpen || !event) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        // Close modal when clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => {
          // Prevent modal from closing when clicking inside the modal content
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
          <div className="absolute top-4 right-4 z-10">
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-white/20 rounded-full transition-colors duration-200 flex items-center justify-center"
              title="ปิด"
            >
              <SafeIcon icon={FiX} className="text-white" size={22} />
            </button>
          </div>
          
          <div className="pr-12">
            <div className="flex items-center space-x-2 mb-2">
              <SafeIcon icon={FiCalendar} size={20} />
              <span className="text-sm font-medium opacity-90">รายละเอียดกิจกรรม</span>
            </div>
            <h2 className="text-2xl font-bold mb-1">{event.title}</h2>
            <div className="flex items-center space-x-2 text-green-100">
              <SafeIcon icon={FiClock} size={16} />
              <span className="text-sm">
                {format(new Date(event.startTime), "eeee, d MMMM yyyy", { locale: th })}
              </span>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-white/5 rounded-full"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Event Type */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-4 h-4 rounded-full ${getEventTypeColor(event.type)}`}></div>
              <span className="text-sm font-semibold text-gray-700">ประเภทกิจกรรม</span>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getEventTypeDisplayColor(event.type)}`}>
              {getEventTypeLabel(event.type)}
            </div>
          </div>

          {/* Time */}
          <div className="bg-gray-50 rounded-2xl p-4">
            <div className="flex items-center space-x-2 mb-3">
              <SafeIcon icon={FiClock} size={16} className="text-gray-600" />
              <span className="text-sm font-semibold text-gray-700">เวลา</span>
            </div>
            {event.isSystem || event.isAllDay ? (
              <p className="text-lg font-bold text-gray-900">ทั้งวัน</p>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">เริ่มต้น</p>
                  <p className="text-lg font-bold text-gray-900">
                    {format(new Date(event.startTime), 'HH:mm')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">สิ้นสุด</p>
                  <p className="text-lg font-bold text-gray-900">
                    {format(new Date(event.endTime), 'HH:mm')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          {event.description && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <SafeIcon icon={FiAlignLeft} size={16} className="text-gray-600" />
                <span className="text-sm font-semibold text-gray-700">รายละเอียด</span>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4">
                <p className="text-gray-700 text-sm leading-relaxed">
                  {event.description}
                </p>
              </div>
            </div>
          )}

          {/* Agent */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <SafeIcon icon={FiUser} size={16} className="text-gray-600" />
              <span className="text-sm font-semibold text-gray-700">
                {event.isSystem ? 'ตัวแทนที่เกี่ยวข้อง' : 'ผู้สร้างกิจกรรม'}
              </span>
            </div>
            <div className="bg-gray-50 rounded-2xl p-4">
              <p className="text-gray-700 text-sm font-medium">
                {event.isSystem ? (event.agentName || 'ระบบ') : getAgentName(event.agent || event.agentId)}
              </p>
            </div>
          </div>

          {/* Close Button */}
          <div className="pt-4">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-2xl hover:bg-gray-200 transition-colors duration-200 font-semibold"
            >
              ปิด
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Delete confirmation dialog component
const DeleteConfirmDialog = ({ isOpen, onClose, onConfirm, eventTitle, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6"
      >
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiTrash2} className="text-red-600" size={20} />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">ลบกิจกรรม</h3>
          <p className="text-gray-500 mb-6">
            คุณต้องการลบกิจกรรม <span className="font-semibold">"{eventTitle}"</span> หรือไม่?
            <br />
            <span className="text-red-500 text-sm">การดำเนินการนี้ไม่สามารถยกเลิกได้</span>
          </p>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
            >
              ยกเลิก
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:bg-red-400 transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>กำลังลบ...</span>
                </>
              ) : (
                <span>ลบกิจกรรม</span>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const TeamCalendar = () => {
  const { user } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Manual state management
  const [events, setEvents] = useState([]);
  const [systemEvents, setSystemEvents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [agentsLoading, setAgentsLoading] = useState(true);

  // Edit and delete states
  const [eventToEdit, setEventToEdit] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, event: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // View event state
  const [viewEventModal, setViewEventModal] = useState({ isOpen: false, event: null });

  // Filter state: 'team' = agent + downlines (default), 'all' = all events
  const [calendarFilter, setCalendarFilter] = useState('team');

  const fetchMyAgentProfile = async () => {
    try {
      const response = await agentAPI.getMyProfile();
      setCurrentAgent(response.data);
    } catch (error) {
      console.log('User is not an agent or error fetching profile');
    }
  };

  const currentUserAgentProfile = useMemo(() => {
    if (currentAgent) return currentAgent;
    if (!user || !agents.length) return null;
    return agents.find(agent => agent.user?.id === user.id);
  }, [user, agents, currentAgent]);

  // Fetch events
  const fetchEvents = async () => {
    try {
      setEventsLoading(true);
      const response = await calendarAPI.getEvents();
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('ไม่สามารถโหลดข้อมูลกิจกรรมได้');
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  // Fetch derived system events (birthdays, license & policy expirations) for the visible month
  const fetchSystemEvents = async (monthDate) => {
    try {
      const month = monthDate.getMonth() + 1;
      const year = monthDate.getFullYear();
      const response = await calendarAPI.getSystemEvents(month, year);
      setSystemEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching system events:', error);
      setSystemEvents([]);
    }
  };

  // Fetch agents
  const fetchAgents = async () => {
    try {
      setAgentsLoading(true);
      const response = await agentAPI.getAgents();
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('ไม่สามารถโหลดข้อมูลเอเจนต์ได้');
      setAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchEvents();
    fetchAgents();
    fetchMyAgentProfile();
  }, []);

  // Reload system events whenever the visible month changes
  useEffect(() => {
    fetchSystemEvents(currentMonth);
  }, [currentMonth]);

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const onDateClick = (day) => setSelectedDate(day);
  const openModal = () => {
    setEventToEdit(null);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEventToEdit(null);
  };

  // Callback to refresh events after creating a new one
  const handleEventCreated = () => {
    fetchEvents();
  };

  // Edit event handler
  const handleEditEvent = (event) => {
    setEventToEdit(event);
    setIsModalOpen(true);
  };

  // Delete event handlers
  const handleDeleteEvent = (event) => {
    setDeleteDialog({ isOpen: true, event });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, event: null });
  };

  const confirmDeleteEvent = async () => {
    if (!deleteDialog.event) return;
    
    setIsDeleting(true);
    
    try {
      await calendarAPI.deleteEvent(deleteDialog.event.id);
      toast.success('ลบกิจกรรมสำเร็จ');
      closeDeleteDialog();
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('ไม่สามารถลบกิจกรรมได้');
    } finally {
      setIsDeleting(false);
    }
  };

  // View event handlers
  const handleViewEvent = (event) => {
    setViewEventModal({ isOpen: true, event });
  };

  const closeViewEventModal = () => {
    setViewEventModal({ isOpen: false, event: null });
  };

  // Check if current user is the owner of the event
  const isEventOwner = (event) => {
    if (!currentUserAgentProfile) return false;
    const eventAgentId = event.agent?.id || event.agentId;
    return eventAgentId === currentUserAgentProfile.id;
  };

  // Build the set of agent IDs in the current user's team (self + downlines)
  const teamAgentIds = useMemo(() => {
    const ids = new Set();
    if (currentUserAgentProfile) {
      ids.add(currentUserAgentProfile.id);
    }
    // agents list (for non-admin) already contains only downlines
    agents.forEach(a => ids.add(a.id));
    return ids;
  }, [currentUserAgentProfile, agents]);

  // User events + derived system events combined for display, filtered by selection
  const combinedEvents = useMemo(() => {
    const all = [...events, ...systemEvents];
    if (calendarFilter === 'all') {
      return all;
    }
    // Filter to only events belonging to the agent and their downlines
    return all.filter(event => {
      const eventAgentId = event.agent?.id || event.agentId;
      if (!eventAgentId) return true; // If no agent info, show it (safety)
      return teamAgentIds.has(eventAgentId);
    });
  }, [events, systemEvents, calendarFilter, teamAgentIds]);

  // Simplified Mobile Calendar Header
  const renderHeader = () => {
    return (
      <div className="mb-4 space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900">
            {format(currentMonth, "MMMM yyyy", { locale: th })}
          </h2>
          <div className="flex space-x-1">
            <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-xl">
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-xl">
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>
        {/* Filter toggle */}
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiFilter} size={14} className="text-gray-400" />
          <div className="flex bg-gray-100 rounded-full p-0.5">
            <button
              onClick={() => setCalendarFilter('team')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                calendarFilter === 'team'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center space-x-1"><SafeIcon icon={FiUser} size={12} /><span>ทีมของฉัน</span></span>
            </button>
            <button
              onClick={() => setCalendarFilter('all')}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                calendarFilter === 'all'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="flex items-center space-x-1"><SafeIcon icon={FiUsers} size={12} /><span>ทั้งหมด</span></span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(currentMonth, { locale: th });
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="text-center text-xs font-medium text-gray-400 py-2">
          {format(addDays(startDate, i), "EEE", { locale: th })}
        </div>
      );
    }
    return <div className="grid grid-cols-7 mb-1">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { locale: th });
    const endDate = endOfWeek(monthEnd, { locale: th });
    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const isSelected = isSameDay(day, selectedDate);
        const isToday = isSameDay(day, new Date());
        const hasEvent = combinedEvents.some(e => isSameDay(new Date(e.startTime), cloneDay));

        days.push(
          <div
            key={day.toString()}
            className="relative h-10 mb-1 flex items-center justify-center cursor-pointer"
            onClick={() => onDateClick(cloneDay)}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition-all
              ${!isSameMonth(day, monthStart)
                ? 'text-gray-300'
                : isSelected
                ? 'bg-blue-600 text-white shadow-md transform scale-110'
                : isToday
                ? 'bg-blue-50 text-blue-600 font-bold'
                : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <span>{format(day, "d")}</span>
            </div>
             {hasEvent && !isSelected && (
              <span className="absolute bottom-1.5 h-1.5 w-1.5 bg-green-500 rounded-full"></span>
            )}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="grid grid-cols-7" key={day.toString()}>{days}</div>
      );
      days = [];
    }
    return <div>{rows}</div>;
  };

  const selectedDayEvents = useMemo(() => {
    return combinedEvents
      .filter(event => isSameDay(new Date(event.startTime), selectedDate))
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [combinedEvents, selectedDate]);

  const getAgentName = (agentOrId) => {
    if (agentOrId && typeof agentOrId === 'object' && agentOrId.firstName) {
      return `${agentOrId.firstName} ${agentOrId.lastName}`;
    }
    const agentId = typeof agentOrId === 'object' ? agentOrId.id : agentOrId;

    if (currentAgent && currentAgent.id === agentId) {
      return `${currentAgent.firstName} ${currentAgent.lastName}`;
    }
    const agent = agents.find(a => a.id === agentId);
    return agent ? `${agent.firstName} ${agent.lastName}` : 'N/A';
  };

  return (
    <>
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-4">
        {renderHeader()}
        {renderDays()}
        {eventsLoading || agentsLoading ? <div>Loading...</div> : renderCells()}
        
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-gray-900">
              {format(selectedDate, "eeee, d MMMM", { locale: th })}
            </h3>
            {currentUserAgentProfile && (
              <button onClick={openModal} className="text-xs text-blue-600 font-medium bg-blue-50 px-3 py-1 rounded-full flex items-center">
                <SafeIcon icon={FiPlus} className="mr-1"/> เพิ่ม
              </button>
            )}
          </div>
          
          <div className="space-y-3">
            {selectedDayEvents.length === 0 ? (
               <div className="text-center py-6 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                <SafeIcon icon={FiCalendar} className="mx-auto text-gray-300 text-2xl mb-2" />
                <p className="text-gray-500 text-sm">ไม่มีกิจกรรม</p>
              </div>
            ) : (
              selectedDayEvents.map(event => (
                <motion.div 
                  key={event.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex p-3 bg-gray-50 rounded-2xl border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                  onClick={() => handleViewEvent(event)}
                >
                  <div className="flex flex-col items-center mr-3 pt-1 min-w-[45px]">
                    {event.isSystem || event.isAllDay ? (
                      <span className="text-xs font-bold text-gray-900 text-center leading-tight">ทั้งวัน</span>
                    ) : (
                      <>
                        <span className="text-xs font-bold text-gray-900">{format(new Date(event.startTime), 'HH:mm')}</span>
                        <div className="w-0.5 h-full bg-gray-200 my-1"></div>
                        <span className="text-xs text-gray-500">{format(new Date(event.endTime), 'HH:mm')}</span>
                      </>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h4 className="text-sm font-bold text-gray-900">{event.title}</h4>
                      <div className="flex items-center space-x-2">
                        <div className={`px-2 py-0.5 inline-block rounded-md text-[10px] font-bold tracking-wider ${getEventTypeDisplayColor(event.type)}`}>
                          {getEventTypeLabel(event.type)}
                        </div>
                        {isEventOwner(event) && (
                          <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditEvent(event);
                              }}
                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-200"
                              title="แก้ไขกิจกรรม"
                            >
                              <SafeIcon icon={FiEdit2} size={14} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEvent(event);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-200"
                              title="ลบกิจกรรม"
                            >
                              <SafeIcon icon={FiTrash2} size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{event.description}</p>
                    <div className="flex items-center text-xs text-gray-400 mt-2">
                      <SafeIcon icon={FiUser} className="mr-1.5"/>
                      <span>{event.isSystem ? (event.agentName || 'ระบบ') : getAgentName(event.agent || event.agentId)}</span>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
      <EventModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        selectedDate={selectedDate}
        agentId={currentUserAgentProfile?.id}
        onEventCreated={handleEventCreated}
        eventToEdit={eventToEdit}
      />
      <DeleteConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDeleteEvent}
        eventTitle={deleteDialog.event?.title || ''}
        isDeleting={isDeleting}
      />
      <ViewEventModal
        isOpen={viewEventModal.isOpen}
        onClose={closeViewEventModal}
        event={viewEventModal.event}
        getAgentName={getAgentName}
      />
    </>
  );
};

export default TeamCalendar;
